import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { fetchPaymentDashboardData } from "../../../../redux/reducers/paymentDashboardSlice";
import "./RevenueReport.css";

const RevenueReport = () => {
  const dispatch = useDispatch();
  const { revenueReport, loading } = useSelector(
    (state) => state.paymentDashboard
  );
  const [view, setView] = useState("YEAR");
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    dispatch(fetchPaymentDashboardData());
  }, [dispatch]);

  useEffect(() => {
    if (!revenueReport || !revenueReport[view]) return;

    const data = revenueReport[view] || [];

    const chartSeries = ["Clinic", "Pharmacy", "Pet Shop"].map((type) => {
      const match = data.find((item) => item.type === type);
      return {
        name: type,
        data: [match?.total || 0],
      };
    });

    // Set categories based on view
    let cats = [];
    if (view === "WEEK") cats = ["This Week"];
    else if (view === "MONTH") cats = ["This Month"];
    else if (view === "YEAR") cats = ["This Year"];

    setSeries(chartSeries);
    setCategories(cats);
  }, [revenueReport, view]);

  const options = {
    chart: {
      type: "line",
      toolbar: { show: false },
    },
    stroke: {
      width: 3,
      curve: "smooth",
      dashArray: [5, 5, 0], // Dashed lines for first two series, solid for third
    },
    markers: {
      size: 6,
      strokeWidth: 2,
      strokeColors: ["#94A3B8", "#F472B6", "#3B82F6"],
      fillColors: ["#94A3B8", "#F472B6", "#3B82F6"],
    },
    xaxis: {
      categories: categories,
    },
    yaxis: {
      labels: {
        formatter: (val) =>
          val >= 1000000
            ? `${(val / 1000000).toFixed(1)}M`
            : val >= 1000
              ? `${(val / 1000).toFixed(1)}k`
              : val,
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "center",
      offsetY: 0,
      markers: {
        width: 12,
        height: 12,
        radius: 6,
        fillColors: ["#94A3B8", "#F472B6", "#3B82F6"],
      },
    },
    title: {
      text: "Revenue Report",
      align: "left",
      style: {
        fontSize: "18px",
        fontWeight: 600,
        color: "#333",
        fontFamily: "inherit",
      },
      offsetY: 0,
      margin: 0,
    },
    colors: ["#94A3B8", "#F472B6", "#3B82F6"],
    grid: {
      show: true,
      strokeDashArray: 3,
      borderColor: "#e0e0e0",
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const category = categories[dataPointIndex] || '';

        // Custom header with gray background
        const customHeader =
          '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
          category +
          "</div>";

        // Build default content structure (all series at this data point) with white background
        let content = '<div style="background-color: #fff; padding: 8px;">';
        series.forEach((s, idx) => {
          const val = s[dataPointIndex];
          if (val !== null && val !== undefined) {
            content +=
              '<div class="apexcharts-tooltip-series-group" style="order: ' + (idx + 1) + '; display: flex;">' +
              // '<span class="apexcharts-tooltip-marker" style="background-color: ' +
              // w.globals.colors[idx] +
              // ';"></span>' +
              '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
              '<div class="apexcharts-tooltip-y-group">' +
              '<span class="apexcharts-tooltip-text-y-label">' +
              w.globals.seriesNames[idx] +
              ': </span>' +
              '<span class="apexcharts-tooltip-text-y-value">' +
              val +
              '</span>' +
              '</div>' +
              '</div>' +
              '</div>';
          }
        });
        content += '</div>';

        return customHeader + content;
      },
    },
  };

  if (loading && !series.length) {
    return (
      <div className="revenue-container">
        <div className="chart-card">Loading chart...</div>
      </div>
    );
  }

  return (
    <div className="revenue-container">
      <div
        className="header"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <Box display="flex" justifyContent="flex-end">
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, val) => val && setView(val)}
            size="small"
            sx={{
              backgroundColor: "#f0f2f5",
              borderRadius: "12px",

              // Styles for all grouped toggle buttons
              "& .MuiToggleButtonGroup-grouped": {
                borderRadius: "7px",
                borderColor: "transparent",
                color: "#333",
                transition: "none", // remove hover effect if needed

                // Only apply to selected button
                "&.Mui-selected": {
                  backgroundColor: "#005EB8",
                  color: "#fff",
                },
              },
            }}
          >
            <ToggleButton value="WEEK">Week</ToggleButton>
            <ToggleButton value="MONTH">Month</ToggleButton>
            <ToggleButton value="YEAR">Year</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </div>

      <div className="chart-card">
        <Chart options={options} series={series} type="line" height={300} />
      </div>
    </div>
  );
};

export default RevenueReport;
